import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/pages/home/components/ArticleCard';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">文章未找到</p>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'PG官网',
      url: 'https://www.pg-official.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.pg-official.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - PG官网`}
        description={article.excerpt}
        canonicalUrl={`https://www.pg-official.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={schema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '文章资讯', href: '/article/pg-guanwang-xinshou-zhinan' },
              { label: article.title },
            ]}
          />
        </div>

        <article className="px-4 md:px-6 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-2">
                {article.title}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">{article.excerpt}</p>
            </div>

            <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - PG官网`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 font-medium whitespace-nowrap">标签：</span>
                {['PG官网', '游戏指南', article.category].map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <section className="py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
              <Link to="/article/pg-guanwang-xinshou-zhinan" className="text-foreground-900">相关文章推荐</Link>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.id} {...ra} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}